// Pixel Forge primitives: panels with title bars, buttons that press, segmented meters, stats.
// Styling lives in styles/global.css (.px-panel, .btn, .meter, .seg, .stat) so plain markup can
// use the same classes.
import type { ButtonHTMLAttributes, ElementType, ReactNode } from 'react';
import { PixelIcon } from './icons';
import type { IconName } from './icons';

export type Tone = 'idle' | 'hot' | 'ok' | 'warn' | 'bad';

export function PixelPanel({
  title,
  titleId,
  icon,
  status,
  tone = 'idle',
  hot = false,
  as: Tag = 'section',
  className,
  bodyClassName,
  flush = false,
  testId,
  children,
  label,
}: {
  title?: ReactNode;
  titleId?: string;
  icon?: IconName;
  /** right-aligned status tag, e.g. "IDLE", "FORGING", "PROOF" */
  status?: ReactNode;
  tone?: Tone;
  /** ember title bar (mining active, proof found) */
  hot?: boolean;
  as?: ElementType;
  className?: string;
  bodyClassName?: string;
  /** no body padding */
  flush?: boolean;
  testId?: string;
  children?: ReactNode;
  label?: string;
}) {
  return (
    <Tag className={`px-panel${className ? ` ${className}` : ''}`} aria-labelledby={title && titleId ? titleId : undefined} aria-label={label} data-testid={testId}>
      {title ? (
        <header className="px-title" data-hot={hot || undefined}>
          <h2 id={titleId}>
            {icon ? <PixelIcon name={icon} px={1} /> : null}
            {title}
          </h2>
          {status ? <StatusTag tone={hot ? 'hot' : tone}>{status}</StatusTag> : null}
        </header>
      ) : null}
      <div className={`px-body${flush ? ' px-flush' : ''}${bodyClassName ? ` ${bodyClassName}` : ''}`}>{children}</div>
    </Tag>
  );
}

export function StatusTag({ tone = 'idle', children, testId }: { tone?: Tone; children: ReactNode; testId?: string }) {
  return (
    <span className="px-tag" data-tone={tone} data-testid={testId}>
      <span aria-hidden="true">■</span> {children}
    </span>
  );
}

export type ButtonVariant = 'primary' | 'molten' | 'ghost' | 'danger' | 'quiet';

/** A chunky button: hover lifts 1 px, active presses 4 px; disabled is dithered with its reason printed below. */
export function PixelButton({
  variant = 'ghost',
  size = 'md',
  block = false,
  icon,
  reason,
  reasonId,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  icon?: IconName;
  /** printed under a disabled button */
  reason?: ReactNode;
  reasonId?: string;
}) {
  const cls = ['btn', `btn-${variant}`, size !== 'md' ? `btn-${size}` : '', block ? 'btn-block' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <>
      <button type="button" className={cls} aria-describedby={rest.disabled && reason && reasonId ? reasonId : undefined} {...rest}>
        {icon ? <PixelIcon name={icon} px={size === 'lg' ? 2 : 1} /> : null}
        <span>{children}</span>
      </button>
      {rest.disabled && reason ? (
        <p className="btn-reason" id={reasonId}>
          {reason}
        </p>
      ) : null}
    </>
  );
}

/**
 * One cell per bit. `value` bits are lit; a fractional remainder shows as a dithered cell.
 * `mark` outlines the target cell. `ramp` colours lit cells along the temper ramp.
 */
export function BitsMeter({
  value,
  cells = 64,
  mark,
  tone = 'molten',
  ramp,
  label,
  testId,
}: {
  value: number;
  cells?: number;
  mark?: number;
  tone?: 'molten' | 'quench' | 'ember' | 'ink';
  /** colour for lit cell i (e.g. temper colour of 2^(i+1)) */
  ramp?: (i: number) => string;
  label: string;
  testId?: string;
}) {
  const v = Math.max(0, Math.min(cells, Number.isFinite(value) ? value : 0));
  const whole = Math.floor(v);
  const frac = v - whole;
  return (
    <div className="meter" data-tone={tone} role="meter" aria-valuemin={0} aria-valuemax={cells} aria-valuenow={Math.round(v * 10) / 10} aria-label={label} data-testid={testId} style={{ gridTemplateColumns: `repeat(${cells}, minmax(0, 1fr))` }}>
      {Array.from({ length: cells }, (_, i) => {
        const on = i < whole;
        const part = i === whole && frac >= 0.25;
        return (
          <span
            key={i}
            data-on={on || undefined}
            data-part={part || undefined}
            data-mark={mark !== undefined && i === Math.max(0, Math.ceil(mark) - 1) ? true : undefined}
            style={on && ramp ? { background: ramp(i) } : undefined}
          />
        );
      })}
    </div>
  );
}

/** Progress as 50-100 segments; filled segments are molten. */
export function SegmentedBar({ value, cells = 50, tone = 'molten', label, testId }: { value: number; cells?: number; tone?: 'molten' | 'quench' | 'ember'; label: string; testId?: string }) {
  const v = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
  const lit = v > 0 ? Math.max(1, Math.round(v * cells)) : 0;
  return (
    <div className="seg" data-tone={tone} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(v * 1000) / 10} aria-label={label} data-testid={testId} style={{ gridTemplateColumns: `repeat(${cells}, minmax(0, 1fr))` }}>
      {Array.from({ length: cells }, (_, i) => (
        <span key={i} data-on={i < lit || undefined} />
      ))}
    </div>
  );
}

/** Silkscreen label, Pixelify value, Departure Mono sub-line. */
export function Stat({ label, value, sub, testId, children }: { label: ReactNode; value: ReactNode; sub?: ReactNode; testId?: string; children?: ReactNode }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value" data-testid={testId}>
        {value}
      </div>
      {children}
      {sub ? <div className="stat-sub">{sub}</div> : null}
    </div>
  );
}
